import React, {
  forwardRef,
} from 'react';
import type {
  ForwardedRef,
  MouseEvent,
  ReactNode,
  KeyboardEvent,
} from 'react';
import clsx from 'clsx';

export type Props = {
  className?: string;
  type?: 'button' | 'submit';
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link' | 'login' | 'text';
  size?: 'default' | 'sm' | 'lg' | 'icon' | 'login';
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  onMouseDown?: (event: MouseEvent<HTMLButtonElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  href?: string;
  children: ReactNode;
  endIcon?: ReactNode;
  disableElevation?: boolean;
  disableRipple?: boolean;
  tabIndex?: number;
};

// Simple class builder to replace cva
type Variant = NonNullable<Props['variant']>;
type Size = NonNullable<Props['size']>;

function buttonVariants(options: { variant?: Variant; size?: Size; className?: string } = {}) {
  const { variant = 'default', size = 'default', className = '' } = options;

  const base = 'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand';

  const byVariant: Record<Variant, string> = {
    default: 'bg-brand text-white hover:bg-brandDark/90',
    destructive: 'bg-red-600 text-white hover:bg-red-700',
    outline: 'border border-gray-200 text-slate-700 hover:bg-slate-50 bg-transparent',
    secondary: 'bg-slate-100 text-slate-900 hover:bg-slate-200',
    ghost: 'bg-transparent hover:bg-slate-50 text-slate-700',
    link: 'text-brand underline-offset-4 hover:underline bg-transparent',
    login: 'bg-brand text-white hover:bg-brandDark',
    text: 'text-brand hover:bg-brand/10 !gap-0 bg-transparent',
  };

  const bySize: Record<Size, string> = {
    default: 'h-10 px-4 py-2',
    sm: 'h-9 rounded-md px-3',
    lg: 'h-11 rounded-md px-8',
    icon: 'h-10 w-10',
    login: 'h-12 px-4 py-2',
  };

  return clsx(base, byVariant[variant], bySize[size], className);
}

const Button = forwardRef(({
  className,
  type = 'button',
  variant = 'default',
  size = 'default',
  onClick,
  onFocus,
  onBlur,
  onKeyDown,
  disabled,
  children,
  endIcon,
  ...props
}: Props, ref: ForwardedRef<HTMLButtonElement>) => {
  const classes = clsx(buttonVariants({ variant, size, className }),  { '!cursor-not-allowed !opacity-60': disabled });
  return (
    <button
      className={classes}
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      ref={ref}
      {...props}
    >
      {children}
      {endIcon && endIcon}
    </button>
  );
});

Button.displayName = 'Button';

// Export both named and default for compatibility
export { Button, buttonVariants };
export default Button;
